import {GovernanceConsultationOverview} from './GovernanceConsultationOverview';
import {PageHeader} from '../ui/PageHeader';
import {useCapability,useStaffSession} from '../ui/StaffSession';
import {useEffect,useState,useRef} from 'react';
import type {FormEvent} from 'react';
import {Link,useParams} from 'react-router';
import {api} from '../api';
import {DateOnlyInput} from '../components/DateOnlyInput';
import {formatDateOnly,formatDateTime} from '../dates';
import {Empty,ErrorState,Loading} from '../ui/State';
import {BuildingAuthorityProfile,type AuthorityProfile} from './BuildingAuthorityProfile';
import {withBuildingScope} from '../ui/building-scope';
import {obligationTemplate,obligationTemplates} from './governance-obligation-templates';

type Option={id:string;name:string};
type Appointment={id:string;client_name:string;managing_agent_name?:string;commencement_date:string;termination_date?:string;status:string};
type Decision={id:string;decision_number:string;title:string;decision_type:string;decision_date:string;summary:string};
type Instruction={id:string;instruction_number:string;title:string;instruction_text:string;received_at:string;authority_status:string};
type Obligation={id:string;obligation_number:string;title:string;obligation_type:string;next_due_date:string;assignee_name?:string;action_id?:string;version:number};
type Workspace={building:{id:string;name:string};authority?:AuthorityProfile;appointments:Appointment[];decisions:Decision[];instructions:Instruction[];obligations:Obligation[];organisations:Option[];users:Option[]};
const value=(f:FormData,n:string)=>String(f.get(n)??'');
const key=()=>crypto.randomUUID();
export function BuildingGovernance(){const canManage=useCapability('governance:manage'),canReadLegal=useCapability('property-legal:view'),canReadWork=useCapability('work:view');
 const{id=''}=useParams();
 const session=useStaffSession(),selectedBuilding=session?.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const[data,setData]=useState<Workspace>();
 const[error,setError]=useState<unknown>();
 const[saving,setSaving]=useState(false);
 const identity=id+':'+(selectedBuilding??'');
 const currentId=useRef(identity);
 currentId.current=identity;
 const load=()=>{if(selectedBuilding&&selectedBuilding!==id){setData(undefined);setError(Error('The governance route does not match the selected building.'));return Promise.resolve()}return api<Workspace>(`/api/buildings/${id}/governance?building=${id}`).then(v=>{if(currentId.current!==identity)return;if(v.building.id!==id)throw Error('Governance records do not match this building.');setData(v);setError(undefined)}).catch(cause=>{if(currentId.current===identity)setError(cause)})};
 useEffect(()=>{setData(undefined);setError(undefined);void load()},[id,selectedBuilding]);
 async function save(path:string,body:unknown){if(!canManage)return;setSaving(true);try{await api(withBuildingScope(path,id),{method:'POST',body:JSON.stringify(body)});await load()}catch(e){setError(e)}finally{setSaving(false)}}if(error&&!data)return <ErrorState error={error}/>;if(!data)return <Loading/>;
 const organisationOptions=data.organisations.map(o=>
<option key={o.id} value={o.id}>{o.name}</option>);
 const appointment=(e:FormEvent<HTMLFormElement>)=>{e.preventDefault();
 const f=new FormData(e.currentTarget);void save('/api/governance/appointments',{developmentId:id,status:'Draft',clientOrganisationId:value(f,'client'),managingAgentOrganisationId:value(f,'agent')||undefined,commencementDate:value(f,'start'),terminationDate:value(f,'end')||undefined,reason:'Management appointment recorded by authorised user',idempotencyKey:key()})};
 const decision=(e:FormEvent<HTMLFormElement>)=>{e.preventDefault();
 const f=new FormData(e.currentTarget);void save('/api/governance/decisions',{developmentId:id,organisationId:value(f,'organisation')||undefined,title:value(f,'title'),decisionType:value(f,'type'),decisionDate:value(f,'date'),summary:value(f,'summary'),reason:'Governance decision recorded by authorised user',idempotencyKey:key()})};
 const instruction=(e:FormEvent<HTMLFormElement>)=>{e.preventDefault();
 const f=new FormData(e.currentTarget);void save('/api/governance/instructions',{developmentId:id,clientOrganisationId:value(f,'client'),receivedAt:new Date(value(f,'received')).toISOString(),title:value(f,'title'),instructionText:value(f,'instruction'),reason:'Client instruction recorded by authorised user',idempotencyKey:key()})};
 const obligation=(e:FormEvent<HTMLFormElement>)=>{e.preventDefault();
 const f=new FormData(e.currentTarget);void save('/api/governance/obligations',{developmentId:id,organisationId:value(f,'organisation')||undefined,obligationType:value(f,'type'),title:value(f,'title'),description:value(f,'description')||undefined,cadenceMonths:Number(value(f,'cadence')),nextDueDate:value(f,'due'),assignedTo:value(f,'owner')||undefined,reason:'Recurring governance obligation recorded',idempotencyKey:key()})};
 return <div className="governance-review-workspace">
<PageHeader title="Governance & consultations" context={data.building.name}
 primaryAction={canReadLegal&&canReadWork?{kind:'link',label:'View consultations',to:`/work/consultations?building=${id}`}:undefined}
 secondaryActions={[{kind:'link',label:'Building overview',to:`/buildings/${id}?building=${id}`}]} />
 {canReadLegal&&canReadWork?<GovernanceConsultationOverview key={id} building={id}/>:<p className="workspace-source-note">Consultation records require the existing legal and Work viewing permissions.</p>}
 <details id="governance-source" className="governance-source-details" onToggle={event=>{const target=new URL(window.location.href);if(event.currentTarget.open&&!target.hash)target.hash='governance-source';else if(!event.currentTarget.open&&target.hash==='#governance-source')target.hash='';window.history.replaceState(window.history.state,'',target)}}>
<summary>Authority, client decisions and recurring administration</summary>{Boolean(error)&&<ErrorState error={error}/>}<div className="governance-workspace">
<BuildingAuthorityProfile profile={data.authority}/>{data.authority?.access==='Available'&&<p>
<Link to={`/buildings/${id}/governance/decisions?building=${id}`}>Decision requests, responses and history</Link>
</p>}<section aria-label="Appointment history">
<h2>Appointment history</h2>{data.appointments.length?<div className="governance-list">{data.appointments.map(a=>
<article className="compact-record" key={a.id}>
<strong>{a.client_name}</strong>
<span>{a.managing_agent_name??'Managing agent not recorded'} · from {formatDateOnly(a.commencement_date)}{a.termination_date?` to ${formatDateOnly(a.termination_date)}`:''} · {a.status}</span>
</article>)}</div>:<Empty>{data.authority?.access==='Restricted'?'Appointment records are restricted for this account.':'No management appointment has been recorded.'}</Empty>}{canManage&&<details>
<summary>Record management appointment</summary>
<p>This form saves a Draft. <Link to={`/buildings/${id}/legal-finance?section=agreement&mode=edit&building=${id}`}>Review the management appointment</Link> with its signed agreement before making it Current.</p>
<form className="compact-form governance-single-form" onSubmit={appointment}>
<label>Client organisation<select name="client" required>
<option value="">Choose client</option>{organisationOptions}</select>
</label>
<label>Managing agent<select name="agent">
<option value="">Not recorded</option>{organisationOptions}</select>
</label>
<label>Commencement<DateOnlyInput name="start" required/>
</label>
<label>Termination<DateOnlyInput name="end"/>
</label>
<button disabled={saving}>Save appointment</button>
</form>
</details>}</section>
 <section id="decisions" tabIndex={-1}>
<h2>Client instructions and decisions</h2>
<div className="governance-list">{data.instructions.map(i=>
<article className="compact-record" key={i.id}>
<strong>{i.title}</strong>
<span>{i.instruction_number} · {formatDateTime(i.received_at)} · authority {i.authority_status}</span>
<p>{i.instruction_text}</p>
</article>)}{data.decisions.map(d=>
<article className="compact-record" key={d.id}>
<strong>{d.title}</strong>
<span>{d.decision_number} · {d.decision_type} · {formatDateOnly(d.decision_date)}</span>
<p>{d.summary}</p>
</article>)}</div>
<div className="governance-forms">{canManage&&<details>
<summary>Record client instruction</summary>
<form className="compact-form" onSubmit={instruction}>
<label>Client<select name="client" required>
<option value="">Choose client</option>{organisationOptions}</select>
</label>
<label>Received<input name="received" type="datetime-local" required/>
</label>
<label>Title<input name="title" required/>
</label>
<label>Instruction<textarea name="instruction" required/>
</label>
<button disabled={saving}>Record instruction</button>
</form>
</details>}{canManage&&<details>
<summary>Record decision</summary>
<form className="compact-form" onSubmit={decision}>
<label>Organisation<select name="organisation">
<option value="">Building-wide</option>{organisationOptions}</select>
</label>
<label>Type<select name="type">
<option>Board decision</option>
<option>Client approval</option>
<option>Written resolution</option>
<option>Management decision</option>
<option>Other</option>
</select>
</label>
<label>Date<DateOnlyInput name="date" required/>
</label>
<label>Title<input name="title" required/>
</label>
<label>Summary<textarea name="summary" required/>
</label>
<button disabled={saving}>Record decision</button>
</form>
</details>}</div>
</section>
 <section id="recurring" tabIndex={-1}>
<h2>Recurring governance administration</h2>
<p>Annual filings, meetings, insurance decisions and company duties can create an Action and Calendar deadline instead of a separate task list.</p>{data.obligations.length?<div className="governance-list">{data.obligations.map(o=>
<article className="compact-record" key={o.id}>
<strong>{o.title}</strong>
<span>{o.obligation_type} · due {formatDateOnly(o.next_due_date)} · {o.assignee_name??'Unassigned'}</span>{o.action_id?<span>Action scheduled</span>:canManage&&<button disabled={saving} onClick={()=>void save(`/api/governance/obligations/${o.id}/schedule`,{reason:'Create the due action and calendar deadline',idempotencyKey:key(),expectedVersion:o.version})}>Create action and deadline</button>}</article>)}</div>:<Empty>No recurring governance obligations have been recorded.</Empty>}{canManage&&<details>
<summary>Add recurring obligation</summary>
<form className="compact-form governance-single-form" onSubmit={obligation}>
<label>Type<input name="type" list="obligation-types" placeholder="Pick a common type or enter your own" required onChange={e=>{const t=obligationTemplate(e.target.value),form=e.currentTarget.form;if(t&&form){const title=form.elements.namedItem('title') as HTMLInputElement,cadence=form.elements.namedItem('cadence') as HTMLInputElement;if(!title.value)title.value=t.title;cadence.value=String(t.cadenceMonths)}}}/>
<datalist id="obligation-types">{obligationTemplates.map(t=>
<option key={t.type} value={t.type}/>)}</datalist>
</label>
 <label>Title<input name="title" required/>
</label>
 <label>Description<textarea name="description"/>
</label>
 <label>Client organisation<select name="organisation">
<option value="">Building-wide</option>{organisationOptions}</select>
</label>
 <label>Repeat every (months)<input name="cadence" type="number" min="1" max="120" defaultValue="12" required/>
</label>
 <label>Next due<DateOnlyInput name="due" required/>
</label>
 <label>Owner<select name="owner">
<option value="">Unassigned</option>{data.users.map(u=>
<option key={u.id} value={u.id}>{u.name}</option>)}</select>
</label>
 <button disabled={saving}>Save obligation</button>
</form>
</details>}</section>
</div>
</details>
</div>}
